import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useCases } from '../../contexts/api-contexts/CasesContext';
import { useAnimalTypes } from '../../contexts/api-contexts/AnimalTypeContext';
import { useBreeds } from '../../contexts/api-contexts/BreedsContext';

interface CaseBasicInfoSummaryProps {
  isCollapsed?: boolean;
  isActive?: boolean;
}

export const CaseBasicInfoSummary: React.FC<CaseBasicInfoSummaryProps> = ({
  isCollapsed = false,
  isActive = false,
}) => {
  const { currentCase } = useCases();
  const { animalTypes } = useAnimalTypes();
  const { breeds, fetchBreedsByAnimalTypeId } = useBreeds();
  const backgroundColor = isActive ? 'rgba(0, 150, 136, 0.12)' : 'transparent';

  // Fetch breeds when animal type changes
  React.useEffect(() => {
    if (currentCase?.patient?.animal_type_id) {
      fetchBreedsByAnimalTypeId(currentCase.patient.animal_type_id);
    }
  }, [currentCase?.patient?.animal_type_id, fetchBreedsByAnimalTypeId]);

  // Format patient information
  const getPatientInfo = () => {
    if (!currentCase?.patient) {
      return 'Patient not yet assigned';
    }

    const patient = currentCase.patient;
    const name = patient.name || 'Unnamed';

    // Find breed name from breed_id
    let breedName = '';
    if (patient.breed_id) {
      // If we have animal_type_id, try to find the breed in that animal type's breeds
      if (patient.animal_type_id && breeds[patient.animal_type_id]) {
        const breedsList = breeds[patient.animal_type_id];
        const breed = breedsList?.find(
          (breed) => breed.id === patient.breed_id
        );
        if (breed) {
          breedName = breed.name;
        }
      } else {
        // If we don't have animal_type_id or can't find the breed in that animal type's breeds,
        // check all animal types for the breed
        Object.values(breeds).forEach((breedsList) => {
          if (breedsList) {
            const breed = breedsList.find(
              (breed) => breed.id === patient.breed_id
            );
            if (breed && !breedName) {
              breedName = breed.name;
            }
          }
        });
      }
    }

    const breedInfo = breedName ? `, ${breedName}` : '';

    // Format animal type
    let animalTypeName = '';
    if (patient.animal_type_id) {
      const animalType = animalTypes.find(
        (type) => type.id === patient.animal_type_id
      );
      if (animalType) {
        animalTypeName = animalType.name;
      }
    }

    const animalTypeInfo = animalTypeName ? ` (${animalTypeName})` : '';

    // Calculate age if date_of_birth is available
    let ageInfo = '';
    if (patient.date_of_birth) {
      const birthDate = new Date(patient.date_of_birth);
      const today = new Date();
      const ageInYears = Math.floor(
        (today.getTime() - birthDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
      );
      ageInfo = `, ${ageInYears} years old`;
    }

    return `${name}${breedInfo}${animalTypeInfo}${ageInfo}`;
  };

  // Collapsed view - just show icon
  if (isCollapsed) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 1.5,
          backgroundColor,
          borderLeft: isActive ? '3px solid #009688' : 'none',
        }}
      >
        <IconButton size="small" color={isActive ? 'primary' : 'default'}>
          <InfoIcon />
        </IconButton>
      </Box>
    );
  }

  // Expanded view
  return (
    <Box
      sx={{
        p: 2,
        backgroundColor,
        borderLeft: isActive ? '3px solid #009688' : 'none',
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <InfoIcon color={isActive ? 'primary' : 'inherit'} sx={{ mr: 1 }} />
        <Typography
          variant="subtitle1"
          color={isActive ? 'primary' : 'inherit'}
        >
          Basic Information
        </Typography>
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ textAlign: 'left' }}
      >
        {getPatientInfo()}
      </Typography>
    </Box>
  );
};

export default CaseBasicInfoSummary;
